import { Fragment, useCallback } from 'react';
import { DotDotDotAshIcon } from '@glu/icons-react';
import locale from '@glu/locale';
import { Button, TERTIARY, EXTRA_SMALL } from '@glu/buttons-react';
import Popover from '@glu/popover-react';
import classnames from 'classnames';
import useStyles from './RowActions.styles';

export const ELLIPSIS = 'ellipsis';
export const FIRST_ACTION = 'first';

export interface IRowAction {
  /** The condition on whether or not to show the action */
  condition?: boolean | ((props: unknown) => boolean);
  /** The onClick handler. Passed back the row data */
  handler?(props: unknown): void;
  /** The label on the action */
  label?: string;
  /** If the action is a link, the href tells it where to go */
  href?: string;
  /** The component for the action. By default a link or button is rendered */
  component?(props: unknown): JSX.Element;
  /** The qa attribute on the default link or button */
  dataQa?: string;
}
export interface IRowActionsProps {
  [key: string]: unknown;
  /** Actions on the row */
  actions?: IRowAction[];
  /** The type of row action interface to render */
  type?: 'first' | 'ellipsis';
}

const popperOptions = {
  modifiers: [
    {
      name: 'offset',
      options: {
        offset: [8, -10],
      },
    },
  ],
};

const emptyArr = [];

const RowActions = (props: IRowActionsProps) => {
  const classes = useStyles();
  const { actions = emptyArr, type = 'first' } = props;
  const visibleActions = actions.filter(
    (action) =>
      action.condition === undefined ||
      (action.condition !== undefined && typeof action.condition === 'function'
        ? action.condition(props)
        : action.condition)
  );
  const [firstAction, ...restActions] = visibleActions;
  const hasOtherActions = restActions.length > 0;
  const getActionContent = useCallback(
    (action: IRowAction, inMenu = false) => {
      if (action.component) {
        return action.component(props);
      }
      return (
        <Button
          className={classnames({
            [classes.menuButton]: inMenu,
            [classes.firstAction]: hasOtherActions && !inMenu,
          })}
          component={action.href ? 'a' : undefined}
          data-qa={action.dataQa}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          href={action.href}
          onClick={
            !action.href && action.handler
              ? () => {
                  action.handler!(props);
                }
              : undefined
          }
          size={EXTRA_SMALL}
          variant={TERTIARY}
        >
          {action.label}
        </Button>
      );
    },
    [classes.firstAction, classes.menuButton, hasOtherActions, props]
  );

  const isFirstActionType = type === FIRST_ACTION;

  if (visibleActions.length === 0) {
    return null;
  }

  return (
    <div className={classnames(classes.wrap, 'action')} data-qa="action-menu-wrapper">
      {isFirstActionType && getActionContent(firstAction)}
      {(hasOtherActions || type === ELLIPSIS) && (
        <Popover
          className={classes.popover}
          content={({ closePopover }) =>
            (isFirstActionType ? restActions : [firstAction, ...restActions]).map((action) => {
              const actionWithClose = {
                ...action,
                handler: action.handler
                  ? () => {
                      action.handler!(props);
                      closePopover();
                    }
                  : undefined,
              };
              return (
                <Fragment key={action.label}>{getActionContent(actionWithClose, true)}</Fragment>
              );
            })
          }
          enablePopoverClick
          noArrow
          placement="bottom-start"
          popperOptions={popperOptions}
        >
          <div>
            <DotDotDotAshIcon
              actionable={{ 'aria-label': locale.get('dataComponents.actions') }}
              className={classes.icon}
            />
          </div>
        </Popover>
      )}
    </div>
  );
};

export default RowActions;
